<template>
	<main class="body" role="main" aria-label="登录页面">
		<el-form
			:model="loginForm"
			:rules="rules"
			ref="loginForm"
			label-position="top"
			label-width="0px"
			class="demo-ruleForm login-container"
			aria-labelledby="login-title"
			aria-describedby="login-instructions"
			novalidate
		>
			<!-- 屏幕阅读器专用的说明文本 -->
			<p id="login-instructions" class="sr-only">
				{{ $t("login.pleaseEnter") }} {{ $t("login.account") }} {{ $t("login.and") }} {{ $t("login.password") }}
			</p>
			<div class="title" id="login-title" role="heading" aria-level="1">{{ $t("login.salesBackend") }}</div>

			<!-- 全局错误通知区域 - 屏幕阅读器主动播报 -->
			<div id="form-error-announcer" class="sr-only" role="alert" aria-live="assertive" aria-atomic="true"></div>

			<el-form-item prop="username" style="width: 100%">
				<label for="username-input" class="field-label">{{ this.loginType === 'sjfood' ? 'SJFOOD' : 'OA' }} {{ $t('login.account') }}</label>
				<el-input
					id="username-input"
					type="text"
					auto-complete="username"
					:placeholder="this.loginType === 'sjfood' ? 'SJFOOD' + $t('login.account') : 'OA' + $t('login.account')"
					@keyup.enter.native="submitForm()"
					v-model="loginForm.username"
					aria-required="true"
					aria-describedby="username-error"
					:class="{'is-error': showUsernameError}"
					@blur="handleBlur('username')"
				></el-input>
				<span
					id="username-error"
					role="alert"
					aria-live="assertive"
					class="error-message"
					:class="{'sr-only': !showUsernameError}"
				>
					<span aria-hidden="true">&#9888;&#65039; </span><span ref="usernameErrorText"></span>
				</span>
			</el-form-item>

			<el-form-item prop="password" style="width: 100%">
				<label for="password-input" class="field-label">{{ this.loginType === 'sjfood' ? 'SJFOOD' : 'OA' }} {{ $t('login.password') }}</label>
				<el-input
					id="password-input"
					type="password"
					auto-complete="current-password"
					:placeholder="this.loginType === 'sjfood' ? 'SJFOOD' + $t('login.password') : 'OA' + $t('login.password')"
					@keyup.enter.native="submitForm()"
					v-model="loginForm.password"
					aria-required="true"
					aria-describedby="password-error"
					:class="{'is-error': showPasswordError}"
					@blur="handleBlur('password')"
				></el-input>
				<span
					id="password-error"
					role="alert"
					aria-live="assertive"
					class="error-message"
					:class="{'sr-only': !showPasswordError}"
				>
					<span aria-hidden="true">&#9888;&#65039; </span><span ref="passwordErrorText"></span>
				</span>
			</el-form-item>

			<el-form-item style="width: 100%">
				<el-button
					type="primary"
					style="width: 100%; margin-top: 25px; min-height: 44px;"
					@click="submitForm()"
					:loading="logining"
					:aria-busy="logining.toString()"
					class="focus-visible-ring login-button"
				>
					{{ logining ? $t('login.loggingIn') : $t("login.login") }}
				</el-button>
			</el-form-item>

			<div class="switch-system-account">
				<el-form-item style="width: 100%">
					<el-link
						:underline="false"
						@click.native="switchLoginType()"
						role="button"
						tabindex="0"
						:aria-label="loginType === 'sjfood' ? $t('login.usingOa') : $t('login.usingSjfood')"
						@keyup.enter.native="switchLoginType()"
						@keyup.space.native="switchLoginType()"
						class="switch-link focus-visible-ring"
					>{{
						loginType === "sjfood" ? $t("login.usingOa") : $t("login.usingSjfood")
					}}</el-link>
				</el-form-item>
				<el-form-item style="width: 100%; display: flex; justify-content: flex-end">
					<el-dropdown
						:aria-label="$t('index.selectLanguage')"
						@command="handleLanguageCommand"
						trigger="click"
					>
						<el-button
							class="el-dropdown-link lang-selector focus-visible-ring"
							type="text"
							role="button"
							tabindex="0"
							aria-haspopup="true"
							:aria-expanded="langDropdownVisible"
							@keyup.enter.native="toggleLangDropdown()"
							@keyup.space.native.prevent="toggleLangDropdown()"
						>
							{{ $t("index.selectLanguage") }}<i class="el-icon-arrow-down el-icon--right" aria-hidden="true"></i>
						</el-button>
						<el-dropdown-menu slot="dropdown" role="menu" :aria-label="$t('index.selectLanguage')">
							<el-dropdown-item
								v-for="lang in languages"
								:key="lang.code"
								:command="lang.code"
								role="menuitem"
								tabindex="0"
								:aria-label="lang.name"
								class="focus-visible-ring"
							>{{ lang.name }}</el-dropdown-item>
						</el-dropdown-menu>
					</el-dropdown>
				</el-form-item>
			</div>
		</el-form>
	</main>
</template>

<script>
import { getToken, setToken, setImToken } from "@/utils/cookies";
import { wsUrl } from "@/config/env";
import { login } from "@/services/api/api";
import { getImToken } from "@/utils/cookies";
import { getVersion } from "@/utils/version";

export default {
	beforeCreate: function () {
		if (getToken()) {
			this.$router.replace({ path: "/" });
		}
	},
	data() {
		return {
			logining: false,
			loginType: "sjfood",
			loginForm: {
				username: "",
				password: "",
			},
			showUsernameError: false,
			showPasswordError: false,
			langDropdownVisible: false,
			languages: [
				{ code: "zh", name: "中文" },
				{ code: "en", name: "English" },
				{ code: "es", name: "Spanish" },
				{ code: "th", name: "Thai" },
				{ code: "vi", name: "Vietnamese" },
				{ code: "fi", name: "Filipino" },
				{ code: "hi", name: "Hindi" },
			],
		};
	},

	mounted() {
		// 修复 Element UI dropdown 的 aria-haspopup 无效值
		// Element UI 硬编码了 aria-haspopup="list"，但 "list" 不是合法的 ARIA 值
		// 合法值: true, false, menu, listbox, tree, grid, dialog
		// 由于 el-dropdown-menu 使用 role="menu"，应修正为 "menu"
		this.$nextTick(() => {
			const dropdownTrigger = this.$el.querySelector('.el-dropdown-link');
			if (dropdownTrigger && dropdownTrigger.getAttribute('aria-haspopup') === 'list') {
				dropdownTrigger.setAttribute('aria-haspopup', 'menu');
			}
		});
	},

	methods: {
		async submitForm() {
			const valid = await this.$refs.loginForm.validate();

			if (valid) {
				this.logining = true;
				this.clearErrors();

				try {
					const result = await login(this.loginForm.username, this.loginForm.password, this.loginType);

					if (result.status === 1) {
						setToken(result.data.token);

						setImToken(result.data.im_token);

						this.$store.commit("SET_USER", result.data.user);

						this.$store.commit("SET_STAFF", result.data.staff);

						// 获取未读消息数量
						this.$store.dispatch("getTotalUnread");

						// 连接websocket服务
						this.$connect(wsUrl + "?token=" + getImToken());

						// 获取版本号
						getVersion().then((version) => (this.versionNumber = version));

						this.$message.success(this.$t("login.loginSuccessful"));

						// 当存在重定向地址时登录成功后去到改地址，否则去到主页
						if (this.$route.query.redirect) {
							this.$router.replace({
								path: this.$route.query.redirect,
							});
						} else {
							this.$router.replace({
								path: "/",
							});
						}
					} else {
						// API 返回错误但未抛出异常
						this.announceError(result.message || this.$t("login.loginFailed"));
					}
				} catch (error) {
					const errorMsg = error.message || error || this.$t("login.loginFailed");
					this.announceError(errorMsg);
					this.$message.error(errorMsg);
				} finally {
					this.logining = false;
				}
			} else {
				// 表单验证失败 - 将 Element UI 验证错误注入到 aria-describedby 目标中
				this.injectValidationErrors();
			}
		},

		/**
		 * 将 Element UI 表单验证错误注入到 aria-describedby 关联的 span 中
		 */
		injectValidationErrors() {
			const announcer = document.getElementById("form-error-announcer");
			const errorMessages = [];

			if (this.$refs.loginForm) {
				const fields = this.$refs.loginForm.fields;
				fields.forEach((field) => {
					if (field.validateState === "error") {
						const errorEl = field.$el.querySelector(".el-form-item__error");
						const errorText = errorEl ? errorEl.textContent : "";
						const errorSpanId = field.prop === "username" ? "username-error" : "password-error";
						const errorSpan = document.getElementById(errorSpanId);

						if (errorSpan && errorText) {
							const textSpan = errorSpan.querySelector("span:last-child");
							if (textSpan) {
								textSpan.textContent = errorText;
							}
							errorSpan.classList.remove("sr-only");
							errorMessages.push(errorText);
						}

						if (field.prop === "username") {
							this.showUsernameError = true;
						} else if (field.prop === "password") {
							this.showPasswordError = true;
						}
					}
				});
			}

			// 将错误信息注入全局 announcer 确保屏幕阅读器主动播报
			if (announcer && errorMessages.length > 0) {
				announcer.textContent = errorMessages.join(". ");
				// 清除 announcer 以便重复播报相同内容
				setTimeout(() => {
					announcer.textContent = "";
				}, 1000);
			}
		},

		/**
		 * 清空所有错误提示
		 */
		clearErrors() {
			this.showUsernameError = false;
			this.showPasswordError = false;

			const usernameError = document.getElementById("username-error");
			const passwordError = document.getElementById("password-error");
			const announcer = document.getElementById("form-error-announcer");

			if (usernameError) {
				usernameError.classList.add("sr-only");
				const textSpan = usernameError.querySelector("span:last-child");
				if (textSpan) textSpan.textContent = "";
			}
			if (passwordError) {
				passwordError.classList.add("sr-only");
				const textSpan = passwordError.querySelector("span:last-child");
				if (textSpan) textSpan.textContent = "";
			}
			if (announcer) {
				announcer.textContent = "";
			}
		},

		/**
		 * 宣布全局错误（如 API 登录失败）
		 */
		announceError(message) {
			const announcer = document.getElementById("form-error-announcer");
			if (announcer && message) {
				announcer.textContent = message;
				setTimeout(() => {
					announcer.textContent = "";
				}, 3000);
			}
		},

		/**
		 * 输入框失焦时清除对应错误状态
		 */
		handleBlur(field) {
			if (field === "username") {
				this.showUsernameError = false;
			} else if (field === "password") {
				this.showPasswordError = false;
			}
		},

		changeLanguage(lang) {
			this.$i18n.locale = lang;
		},

		handleLanguageCommand(command) {
			this.changeLanguage(command);
			this.langDropdownVisible = false;
		},

		toggleLangDropdown() {
			this.langDropdownVisible = !this.langDropdownVisible;
		},

		switchLoginType() {
			this.loginType = this.loginType === "sjfood" ? "oa" : "sjfood";
			this.logining = false;
		},
	},
	computed: {
		rules() {
			return {
				username: [
					{
						required: true,
						message: this.$t("login.pleaseEnter") + (this.loginType === "sjfood" ? " SJFOOD " : " OA ") + this.$t("login.userName"),
						trigger: "blur",
					},
				],
				password: [
					{
						required: true,
						message: this.$t("login.pleaseEnter") + (this.loginType === "sjfood" ? " SJFOOD " : " OA ") + this.$t("login.password"),
						trigger: "blur",
					},
				],
			};
		},
	},
};
</script>

<style lang="scss" scoped>
// 屏幕阅读器专用样式 - 隐藏但可被屏幕阅读器访问
.sr-only {
	position: absolute;
	width: 1px;
	height: 1px;
	padding: 0;
	margin: -1px;
	overflow: hidden;
	clip: rect(0, 0, 0, 0);
	white-space: nowrap;
	border: 0;
}

// 焦点可见样式 - 确保键盘导航时有清晰的焦点指示器
.focus-visible-ring:focus-visible {
	outline: 3px solid #1D6BCE;
	outline-offset: 2px;
}

// 输入框标签 - 可见标签（替代原来的 sr-only）
.field-label {
	display: block;
	font-size: 14px;
	color: #333;
	margin-bottom: 4px;
	font-weight: 500;
}

// 错误提示消息
.error-message {
	display: block;
	font-size: 12px;
	color: #d9534f;
	margin-top: 2px;
	line-height: 1.5;
}

// 切换登录类型链接
.switch-link {
	font-size: 14px;
	min-height: 44px;
	display: inline-flex;
	align-items: center;
}

// 语言选择器按钮
.lang-selector {
	min-height: 44px;
	display: inline-flex;
	align-items: center;
}

// 登录按钮
.login-button {
	font-size: 16px;
	font-weight: 500;
}

// ==========================================
// WCAG 对比度修复
// 问题: Element UI 默认主题色 #409EFF 相对亮度约 0.34
//       → 白色文字在 #409EFF 上: 2.78:1 (需 ≥4.5:1)
//       → #409EFF 文字在白色背景上: 2.78:1 (需 ≥4.5:1)
// 方案: 使用更深的蓝色，确保所有状态对比度 ≥ 4.5:1
// ==========================================

// 1) 登录按钮 - 白色文字在深蓝背景上
.login-container ::v-deep .el-button--primary {
	background-color: #1D6BCE;
	border-color: #1D6BCE;

	&:hover,
	&:focus {
		background-color: #266FCB;
		border-color: #266FCB;
	}

	&:active {
		background-color: #185CB3;
		border-color: #185CB3;
	}
}

// 2) 语言选择器 - 深蓝文字在白色背景上
.login-container ::v-deep .lang-selector {
	color: #2563EB;
}

.login-container {
	-webkit-border-radius: 1px;
	border-radius: 1px;
	-moz-border-radius: 1px;
	background-clip: padding-box;
	margin: 180px auto;
	width: 350px;
	padding: 35px 35px 15px 35px;
	background: #fff;
	border: 1px solid #eaeaea;

	.title {
		font-size: 24px;
		margin: 0px auto 40px auto;
		text-align: center;
		font-weight: 400;
		color: #333;
	}

	.switch-system-account {
		display: flex;
		align-items: center;
	}
}

.body {
	// background-image: url("http://passtest.wiltechs.com/Content/images/login_bg.jpg");
	position: fixed;
	right: 0;
	left: 0;
	top: 0;
	bottom: 0;
	z-index: 1000;
	background-repeat: no-repeat;
	background-size: 100% 100%;
	-moz-background-size: 100% 100%;
	background-position: center center;
	background-attachment: fixed;
	background-size: cover;
	background-color: #464646;
}
</style>
